import React from 'react';
import SEO from '../components/SEO';

const Cookies: React.FC = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Cookie Policy',
    description: 'Cookie Policy for JackpotQuantum.com - Learn about how we use cookies and similar technologies on our website.',
  };

  return (
    <>
      <SEO
        title="Cookie Policy - JackpotQuantum.com"
        description="Read our Cookie Policy to understand how JackpotQuantum.com uses cookies and similar tracking technologies to enhance your browsing experience."
        canonical="/cookies"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-dark-900">
        <section className="py-20 bg-gradient-to-br from-dark-800 to-dark-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="text-gradient">Cookie</span> <span className="text-white">Policy</span>
              </h1>
              <p className="text-gray-400">Last updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
        </section>

        <section className="py-20 bg-dark-800">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto glass rounded-2xl p-8 md:p-12">
              <div className="prose prose-invert max-w-none">
                <h2 className="text-3xl font-bold text-white mb-6">1. What Are Cookies?</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are 
                  widely used to make websites work more efficiently and provide information to website owners. Cookies allow a website 
                  to recognize your device and store some information about your preferences or past actions.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">2. How We Use Cookies</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  We use cookies and similar tracking technologies to track activity on our website and store certain information. 
                  The information we collect through cookies helps us:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                  <li>Remember your preferences and settings</li>
                  <li>Understand how you use our website</li>
                  <li>Improve our website's functionality and user experience</li>
                  <li>Analyze website traffic and user behavior</li>
                  <li>Personalize content and advertisements</li>
                  <li>Track affiliate referrals and commissions</li>
                </ul>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">3. Types of Cookies We Use</h2>
                
                <h3 className="text-2xl font-semibold text-white mb-4 mt-8">3.1 Essential Cookies</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  These cookies are necessary for the website to function properly. They enable core functionality such as security, 
                  network management, and accessibility. You cannot opt-out of these cookies.
                </p>

                <h3 className="text-2xl font-semibold text-white mb-4 mt-8">3.2 Analytics Cookies</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  These cookies help us understand how visitors interact with our website by collecting and reporting information 
                  anonymously. We use services like Google Analytics to track page views, user journeys, and website performance.
                </p>

                <h3 className="text-2xl font-semibold text-white mb-4 mt-8">3.3 Functional Cookies</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  These cookies allow the website to remember choices you make (such as your username, language, or region) and provide 
                  enhanced, personalized features. They may also be used to provide services you have requested.
                </p>

                <h3 className="text-2xl font-semibold text-white mb-4 mt-8">3.4 Marketing Cookies</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  These cookies are used to track visitors across websites to display relevant advertisements. They are also used to 
                  limit the number of times you see an advertisement and measure the effectiveness of advertising campaigns.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">4. Third-Party Cookies</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  In addition to our own cookies, we may also use various third-party cookies to report usage statistics of the website, 
                  deliver advertisements, and so on. These third parties may include:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                  <li><strong className="text-white">Google Analytics:</strong> For website analytics and performance tracking</li>
                  <li><strong className="text-white">Affiliate Networks:</strong> For tracking referrals and commissions</li>
                  <li><strong className="text-white">Social Media Platforms:</strong> For social sharing and embedded content</li>
                  <li><strong className="text-white">Advertising Networks:</strong> For displaying relevant advertisements</li>
                </ul>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">5. Managing Cookies</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  You have the right to accept or reject cookies. Most web browsers automatically accept cookies, but you can usually 
                  modify your browser settings to decline cookies if you prefer. However, this may prevent you from taking full 
                  advantage of our website.
                </p>
                <p className="text-gray-300 leading-relaxed mb-6">
                  You can manage cookies through your browser settings. Here are links to instructions for popular browsers:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6 ml-4">
                  <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-quantum-400 hover:underline">Google Chrome</a></li>
                  <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" target="_blank" rel="noopener noreferrer" className="text-quantum-400 hover:underline">Mozilla Firefox</a></li>
                  <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-quantum-400 hover:underline">Safari</a></li>
                  <li><a href="https://support.microsoft.com/en-us/windows/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer" className="text-quantum-400 hover:underline">Microsoft Edge</a></li>
                </ul>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">6. Cookie Consent</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  When you first visit our website, we will ask for your consent to use non-essential cookies. You can accept all 
                  cookies, reject non-essential cookies, or customize your preferences. You can change your cookie preferences at any 
                  time by clicking the cookie settings link in our cookie banner or footer.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">7. Do Not Track Signals</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Some browsers include a "Do Not Track" (DNT) feature that signals to websites you visit that you do not want to have 
                  your online activity tracked. Currently, there is no standard for how DNT signals should be interpreted. As a result, 
                  we do not currently respond to DNT browser signals or mechanisms.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">8. Updates to This Cookie Policy</h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, 
                  legal, or regulatory reasons. We will notify you of any material changes by posting the new Cookie Policy on this 
                  page and updating the "Last updated" date.
                </p>

                <h2 className="text-3xl font-bold text-white mb-6 mt-12">9. Contact Us</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  If you have any questions about our use of cookies, please contact us:
                </p>
                <div className="bg-dark-700 rounded-lg p-6 mb-6">
                  <p className="text-gray-300">
                    <strong className="text-white">Email:</strong> privacy@jackpotquantum.com<br />
                    <strong className="text-white">Website:</strong> jackpotquantum.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Cookies;

